import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { Reveal, MaskedLines } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { EVENT_SCHEMA } from "../data/seo";
import { api } from "../lib/api";
import { RETREAT, IMAGES, RETREAT_GALLERY } from "../data/site";
import { Check, Loader2, Home, Utensils, Car, Gift, HeartHandshake, ArrowUpRight, Flame } from "lucide-react";

const INCLUDE_ICONS = [Home, Utensils, Car, Gift, HeartHandshake];

export default function Retreats() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const [plan, setPlan] = useState("retreat_full");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [spots, setSpots] = useState(null);
  const [wl, setWl] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [wlSent, setWlSent] = useState(false);
  const [wlLoading, setWlLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setWlF = (k) => (e) => setWl({ ...wl, [k]: e.target.value });

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (!wl.first_name || !wl.last_name || !wl.email) { toast.error("Please share your name and email."); return; }
    setWlLoading(true);
    try { await api.post("/retreat/waitlist", wl); setWlSent(true); toast.success("You're on the list — we'll be in touch."); }
    catch { toast.error("Something went wrong. Please try again."); }
    finally { setWlLoading(false); }
  };

  useEffect(() => { api.get("/retreat/spots").then((r) => setSpots(r.data)).catch(() => {}); }, []);

  const soldOut = spots && spots.remaining <= 0;

  const register = async (e) => {
    e.preventDefault();
    if (soldOut) { toast.error("Retreat is fully booked."); return; }
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const reg = await api.post("/retreat/register", { ...form, plan });
      const checkout = await api.post("/payments/checkout", {
        lookup_key: plan,
        origin_url: window.location.origin,
        registration_id: reg.data.registration_id,
        registration_type: "retreat",
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to start checkout. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div data-testid="retreats-page">
      <SEO
        title="Renewed & Restored | Christian Women's Therapy Retreat FL"
        description="A 4-day Christian women's therapy intensive retreat, May 14–17, 2027 in Clearwater, FL. All-inclusive $1,500 with payment plans. Reserve your spot."
        path="/retreats"
        jsonLd={EVENT_SCHEMA}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.img src={IMAGES.retreatHero} alt="Serene beach" style={{ y: heroY }} className="absolute inset-0 w-full h-[130%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/30 to-navy/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full text-cream pt-24">
          <Reveal>
            <p className="uppercase tracking-[0.35em] text-xs text-goldbright mb-4">Beyond Blessed Presents · 2027</p>
          </Reveal>
          <p className="font-script text-6xl md:text-8xl text-goldbright leading-none mb-4">Renewed &amp; Restored</p>
          <h1 className="font-serif text-3xl md:text-5xl leading-tight max-w-3xl">
            <MaskedLines lines={["A Christian Women's", "Therapy Intensive Retreat"]} />
          </h1>
          <p className="font-serif italic text-2xl md:text-3xl text-seafoam mt-6">{RETREAT.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-6 text-cream/85">
            <span className="border border-cream/25 rounded-full px-5 py-2">{RETREAT.dates}</span>
            <span className="border border-cream/25 rounded-full px-5 py-2">{RETREAT.location}</span>
            {spots && (
              <span data-testid="hero-spots-badge" className={`rounded-full px-5 py-2 inline-flex items-center gap-2 ${soldOut ? "bg-navy/50 text-cream/70" : "bg-goldbright text-navy font-medium"}`}>
                <Flame size={15} /> {soldOut ? "Fully booked — join waitlist" : `Only ${spots.remaining} of ${spots.max} spots left`}
              </span>
            )}
          </div>
          <a href="#register" data-testid="hero-register-scroll" className="mt-9 inline-flex items-center gap-2 bg-goldbright text-navy px-8 py-4 rounded-full hover:bg-cream transition-colors font-medium">
            Register today <ArrowUpRight size={18} />
          </a>
        </div>
      </section>

      {/* INTRO + PILLARS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <Reveal>
          <p className="font-serif italic text-3xl md:text-4xl text-teal text-center max-w-3xl mx-auto leading-relaxed">{RETREAT.intro}</p>
          <p className="text-center font-script text-4xl text-gold mt-8">{RETREAT.scripture}</p>
        </Reveal>
        <div className="grid md:grid-cols-4 gap-5 mt-16">
          {RETREAT.pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full p-8 bg-seafoam/20 border border-teal/20 text-center">
                <div className="w-12 h-12 rounded-full bg-teal text-cream flex items-center justify-center mx-auto mb-5 font-serif text-lg">{i + 1}</div>
                <h3 className="font-serif text-xl text-navy mb-3">{p.title}</h3>
                <p className="text-charcoal/70 text-sm leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INCLUDES */}
      <section className="bg-navy text-cream py-24" data-testid="retreat-includes">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-goldbright mb-14 text-center">Your retreat includes</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {RETREAT.includes.map((inc, i) => {
              const Icon = INCLUDE_ICONS[i % INCLUDE_ICONS.length];
              return (
                <Reveal key={inc} delay={i * 0.06}>
                  <div className="text-center px-4">
                    <Icon className="text-goldbright mx-auto mb-4" size={30} />
                    <p className="text-cream/85 text-sm">{inc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-cream py-24" data-testid="retreat-gallery">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <h2 className="font-serif text-4xl md:text-5xl text-navy">A glimpse of what awaits</h2>
              <p className="text-charcoal/60 max-w-sm text-sm italic">Sisterhood, stillness, and shoreline moments.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {RETREAT_GALLERY.map((g, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}>
                <figure className={`group overflow-hidden bg-white p-2 ${i % 5 === 0 ? "md:row-span-2 md:col-span-1" : ""}`}>
                  <div className={`overflow-hidden ${i % 5 === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
                    <img src={g.url} alt={g.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <figcaption className="text-xs uppercase tracking-widest text-charcoal/50 pt-3 pb-1 text-center">{g.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-28" data-testid="retreat-itinerary">
        <Reveal><h2 className="font-serif text-4xl md:text-5xl text-navy mb-16 text-center">Retreat itinerary</h2></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {RETREAT.itinerary.map((d, i) => (
            <Reveal key={d.day} delay={i * 0.1}>
              <div className="border-t-2 border-gold pt-6">
                <h3 className="font-serif text-2xl text-teal mb-6">{d.day}</h3>
                <ul className="space-y-5">
                  {d.items.map(([time, label], j) => (
                    <li key={j} className="flex gap-4">
                      <span className="text-gold font-medium text-sm w-20 shrink-0">{time}</span>
                      <span className="text-charcoal/75 text-sm">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REGISTRATION + PAYMENT */}
      <section id="register" className="bg-seafoam/20 py-28" data-testid="retreat-register">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-center mb-14">
              <p className="uppercase tracking-[0.3em] text-xs text-teal mb-3">
                {soldOut ? "Retreat is Full · Join the Waitlist" : "Reserve Your Place · Spaces Limited"}
              </p>
              <h2 className="font-serif text-4xl md:text-6xl text-navy">
                {soldOut ? "Join the retreat waitlist" : "Register for the retreat"}
              </h2>
              <p className="text-charcoal/70 mt-4 max-w-xl mx-auto">
                {soldOut
                  ? "This year's retreat is fully booked. Share your details and we'll let you know the moment a spot opens or when the next retreat is announced."
                  : "Total investment is $1,500, all-inclusive. Choose a payment plan that works for you and secure your spot with your first payment today."}
              </p>
            </div>
          </Reveal>

          {soldOut ? (
            <Reveal>
              {wlSent ? (
                <div className="bg-white p-10 border border-gold/25 max-w-xl mx-auto text-center" data-testid="waitlist-confirm">
                  <p className="font-script text-4xl text-gold mb-2">Thank you</p>
                  <h3 className="font-serif text-2xl text-navy mb-3">You're on the list</h3>
                  <p className="text-charcoal/70">We'll be in touch as soon as a place opens up.</p>
                </div>
              ) : (
                <form onSubmit={joinWaitlist} className="bg-white p-8 md:p-12 border border-gold/25 max-w-2xl mx-auto" data-testid="waitlist-form">
                  <div className="grid sm:grid-cols-2 gap-8 mb-8">
                    <input className="intake-input" placeholder="First name *" value={wl.first_name} onChange={setWlF("first_name")} data-testid="wl-first-name" />
                    <input className="intake-input" placeholder="Last name *" value={wl.last_name} onChange={setWlF("last_name")} data-testid="wl-last-name" />
                    <input className="intake-input" placeholder="Email *" type="email" value={wl.email} onChange={setWlF("email")} data-testid="wl-email" />
                    <input className="intake-input" placeholder="Phone" value={wl.phone} onChange={setWlF("phone")} data-testid="wl-phone" />
                  </div>
                  <div className="text-center">
                    <button type="submit" disabled={wlLoading} data-testid="waitlist-submit" className="bg-navy text-cream px-9 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                      {wlLoading ? <><Loader2 size={17} className="animate-spin" /> Joining…</> : <>Notify me when a spot opens <ArrowUpRight size={17} /></>}
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          ) : (
            <>
          {/* Plans */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="retreat-plans">
            {RETREAT.plans.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPlan(p.key)}
                data-testid={`plan-${p.key}`}
                className={`text-left p-7 border transition-all duration-300 relative ${
                  plan === p.key ? "bg-navy text-cream border-navy" : "bg-white border-gold/25 hover:border-teal"
                }`}
              >
                {p.featured && <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest bg-goldbright text-navy px-2 py-1 rounded-full">Best value</span>}
                <p className={`text-sm mb-2 ${plan === p.key ? "text-goldbright" : "text-teal"}`}>{p.label}</p>
                <p className="font-serif text-3xl">{p.price}</p>
                <p className={`text-xs mt-1 ${plan === p.key ? "text-cream/60" : "text-charcoal/50"}`}>{p.sub}</p>
                {plan === p.key && <Check className="absolute bottom-5 right-5 text-goldbright" size={20} />}
              </button>
            ))}
          </div>

          {/* Form */}
          <Reveal>
            <form onSubmit={register} className="bg-white p-8 md:p-12 border border-gold/25 max-w-3xl mx-auto" data-testid="retreat-form">
              <div className="grid sm:grid-cols-2 gap-8 mb-8">
                <input className="intake-input" placeholder="First name *" value={form.first_name} onChange={set("first_name")} data-testid="reg-first-name" />
                <input className="intake-input" placeholder="Last name *" value={form.last_name} onChange={set("last_name")} data-testid="reg-last-name" />
                <input className="intake-input" placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="reg-email" />
                <input className="intake-input" placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="reg-phone" />
              </div>
              <textarea className="intake-input resize-none mb-8" rows={3} placeholder="Anything we should know? (dietary needs, questions...)" value={form.notes} onChange={set("notes")} data-testid="reg-notes" />
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <p className="text-charcoal/60 text-sm">You'll be redirected to secure Stripe checkout for your <span className="text-teal font-medium">{RETREAT.plans.find((p) => p.key === plan)?.price}</span> payment.</p>
                <button type="submit" disabled={loading || soldOut} data-testid="retreat-register-button" className="bg-navy text-cream px-9 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60 shrink-0">
                  {soldOut ? "Fully booked" : loading ? <><Loader2 size={17} className="animate-spin" /> Redirecting…</> : <>Register &amp; Pay <ArrowUpRight size={17} /></>}
                </button>
              </div>
            </form>
          </Reveal>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
